if (!window.Eurus.loadedScript.has('product-subscription-bundle.js')) {
  window.Eurus.loadedScript.add('product-subscription-bundle.js');

  (() => {
    const bundleCache = new Map();
    const productCache = new Map();
    const productMetaCache = new Map();
    const selector = '[data-subscription-bundle]';

    // Default English strings — used as a safe fallback when a key
    // is not provided by the Liquid config. The Liquid template injects
    // translated values via config.strings from block.settings.
    // Placeholders use [token] syntax because Shopify's customizer
    // interprets {{ ... }} in schema defaults as dynamic source bindings.
    const DEFAULT_STRINGS = {
      addProduct: 'Add product',
      addPlus: 'Add +',
      decreaseQuantity: 'Decrease quantity',
      increaseQuantity: 'Increase quantity',
      subscribeAndSave: 'Subscribe & Save',
      save: 'Save [percent]%',
      servingsSummary: '[price] /day | [count] Servings',
      emptyStateAtc: 'Add your flavours',
      addYourFlavours: 'Add your flavours',
      addToCart: 'Add to cart',
      errorBundleMissing: 'Bundle configuration is missing.',
      errorBundleLoad: 'Unable to load bundle configuration.',
      errorBoxSize: 'No bundle size is available for the selected quantity.',
      errorSelectFlavour: 'Select at least 1 flavour before adding this bundle.',
      errorTransaction: 'Bundle transaction failed.',
      errorAddToCart: 'Unable to add this bundle to cart.'
    };

    // Replace [token] placeholders in a translated string with values.
    // We use square-bracket syntax because Shopify's theme customizer
    // treats {{ ... }} in schema defaults as dynamic source references.
    const interpolate = (template, values = {}) => {
      if (!template) return '';
      return String(template).replace(/\[(\w+)\]/g, (match, key) => {
        return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
      });
    };

    const resolvePlanDescription = (plan, locale, configuredDescription) => {
      const lc = String(locale || '').toLowerCase();
      if ((lc === 'fr' || lc.startsWith('fr-')) && configuredDescription) {
        return configuredDescription;
      }
      if (!plan) return '';
      return plan.description || '';
    };

    class ProductSubscriptionBundle {
      constructor(root) {
        this.root = root;
        this.config = this.readConfig();
        this.strings = { ...DEFAULT_STRINGS, ...(this.config.strings || {}) };
        this.bundle = null;
        this.products = [];
        this.loading = false;
        this.bundleLoading = false;
        this.purchaseType = 'subscription';
        this.selectedSellingPlanId = null;
        this.selectedServingQty = Number(this.config.defaultServingQty || 3);
        this.quantities = {};
        this.maxTotal = Number(this.config.fallbackMaxTotal || this.selectedServingQty || 3);
        this.minRequiredQty = 1;
        this.totalQty = 0;
        this.root.dataset.initialized = 'true';
        this.cacheElements();
        this.minRequiredQty = this.resolveMinRequiredQty();
        this.bindEvents();
        this.render();
        this.loadBundle();
      }

      // Helper to look up a translated string with optional placeholder values.
      t(key, values) {
        return interpolate(this.strings[key], values);
      }

      readConfig() {
        const script = this.root.querySelector('[data-bundle-config]');
        if (!script) return { bundleId: '', bundleUrl: '', products: [], strings: {} };
        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          console.error('Invalid subscription bundle config.', error);
          return { bundleId: '', bundleUrl: '', products: [], strings: {} };
        }
      }

      cacheElements() {
        this.errorEl = this.root.querySelector('[data-bundle-error]');
        this.productsContainer = this.root.querySelector('[data-bundle-products]');
        this.atcButton = this.root.querySelector('[data-bundle-atc]');
        this.atcText = this.root.querySelector('[data-bundle-atc-text]');
        this.atcSpinner = this.root.querySelector('[data-bundle-atc-spinner]');
        this.summaryEl = this.root.querySelector('[data-bundle-summary]');
        this.summaryPriceEl = this.root.querySelector('[data-bundle-summary-price]');
        this.summaryCompareEl = this.root.querySelector('[data-bundle-summary-compare]');
        this.frequencyWrap = this.root.querySelector('[data-frequency-wrap]');
        this.frequencySelect = this.root.querySelector('[data-frequency-select]');
        this.planDescription = this.root.querySelector('[data-plan-description]');
        this.loadingEl = this.root.querySelector('[data-bundle-loading]');
        this.purchaseButtons = Array.from(this.root.querySelectorAll('[data-purchase-option]'));
        this.purchaseCards = Array.from(this.root.querySelectorAll('[data-purchase-card]'));
        this.servingCards = Array.from(this.root.querySelectorAll('[data-serving-card]'));
        this.subscribeTitle = this.root.querySelector('[data-subscribe-title]');
        this.subscribeSubtitle = this.root.querySelector('[data-subscribe-subtitle]');
        this.onetimeSubtitle = this.root.querySelector('[data-onetime-subtitle]');

        const sectionRoot = this.root.closest('[id^="shopify-section-"], .shopify-section, section') || document;
        this.mainPriceEl = sectionRoot.querySelector('.x-block-price .main-product-price');
      }

      bindEvents() {
        this.root.addEventListener('click', (event) => {
          const addButton = event.target.closest('[data-qty-add]');
          const plusButton = event.target.closest('[data-qty-plus]');
          const minusButton = event.target.closest('[data-qty-minus]');

          if (addButton) {
            const key = addButton.getAttribute('data-qty-add');
            this.setQuantity(key, (this.quantities[key] || 0) + 1);
            return;
          }

          if (plusButton) {
            const key = plusButton.getAttribute('data-qty-plus');
            this.setQuantity(key, (this.quantities[key] || 0) + 1);
            return;
          }

          if (minusButton) {
            const key = minusButton.getAttribute('data-qty-minus');
            this.setQuantity(key, (this.quantities[key] || 0) - 1);
          }
        });

        this.purchaseButtons.forEach((button) => {
          button.addEventListener('click', () => {
            this.purchaseType = button.getAttribute('data-purchase-option');
            this.clearError();
            this.render();
          });
        });

        this.servingCards.forEach((card) => {
          card.addEventListener('click', () => {
            const quantity = Number(card.getAttribute('data-serving-card'));
            if (!quantity) return;
            if (this.totalQuantity() > quantity) {
              this.resetQuantities();
            }
            this.selectedServingQty = quantity;
            this.clearError();
            this.render();
          });
        });

        if (this.frequencySelect) {
          this.frequencySelect.addEventListener('change', () => {
            this.selectedSellingPlanId = Number(this.frequencySelect.value) || null;
            this.render();
          });
        }

        if (this.atcButton) {
          this.atcButton.addEventListener('click', () => this.addBundleToCart());
        }
      }

      static fetchBundleData(bundleId, locale) {
        // Loop's storefront API doesn't publicly document its localization
        // parameter, so we send the locale via the two most common patterns
        // (query string + Accept-Language header). Unrecognized signals
        // are safely ignored. Cache key includes locale so switching
        // language doesn't return the previous cached response.
        const params = new URLSearchParams();
        if (locale) params.set('locale', locale);
        const qs = params.toString();
        const apiUrl = `https://api.loopsubscriptions.com/storefront/2023-10/bundle/${encodeURIComponent(bundleId)}${qs ? `?${qs}` : ''}`;
        const cacheKey = `${apiUrl}|${locale || ''}`;

        if (bundleCache.has(cacheKey)) return bundleCache.get(cacheKey);

        const headers = { Accept: 'application/json' };
        if (locale) headers['Accept-Language'] = locale;

        const request = fetch(apiUrl, { headers })
          .then(async (response) => {
            const contentType = response.headers.get('content-type') || '';
            const payload = contentType.includes('application/json')
              ? await response.json()
              : null;

            if (response.ok) {
              const bundle = payload && payload.data
                ? (payload.data.bundle || payload.data)
                : payload;

              if (!bundle) throw new Error('Loop bundle data is missing.');
              return bundle;
            }

            const error = new Error((payload && payload.message) || `Loop bundle API request failed with ${response.status}`);
            error.code = payload && payload.code ? payload.code : null;
            throw error;
          });

        bundleCache.set(cacheKey, request);
        return request;
      }

      static fetchProductData(handle) {
        const normalizedHandle = String(handle || '').trim();
        if (!normalizedHandle) return Promise.resolve(null);

        const cacheKey = normalizedHandle.toLowerCase();
        if (productCache.has(cacheKey)) return productCache.get(cacheKey);

        const request = fetch(`${window.Shopify.routes.root}products/${encodeURIComponent(normalizedHandle)}.js`, {
          headers: { Accept: 'application/json' }
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Product data request failed with ${response.status}`);
            }

            return response.json();
          })
          .catch((error) => {
            console.warn(`Unable to load product data for "${normalizedHandle}".`, error);
            return null;
          });

        productCache.set(cacheKey, request);
        return request;
      }

      static fetchProductMeta(handle) {
        const normalizedHandle = String(handle || '').trim();
        if (!normalizedHandle) return Promise.resolve(null);

        const cacheKey = normalizedHandle.toLowerCase();
        if (productMetaCache.has(cacheKey)) return productMetaCache.get(cacheKey);

        const request = fetch(`${window.Shopify.routes.root}products/${encodeURIComponent(normalizedHandle)}`, {
          headers: { Accept: 'text/html' }
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Product page request failed with ${response.status}`);
            }

            return response.text();
          })
          .then((htmlText) => {
            const html = new DOMParser().parseFromString(htmlText, 'text/html');
            const script = html.querySelector('[data-product-bundle-meta]');
            if (!script) return null;

            try {
              return JSON.parse(script.textContent);
            } catch (error) {
              console.warn(`Invalid product bundle meta for "${normalizedHandle}".`, error);
              return null;
            }
          })
          .catch((error) => {
            console.warn(`Unable to load product meta for "${normalizedHandle}".`, error);
            return null;
          });

        productMetaCache.set(cacheKey, request);
        return request;
      }

      async loadBundle() {
        if (!this.config.bundleId) {
          this.setError(this.t('errorBundleMissing'));
          return;
        }

        this.bundleLoading = true;
        this.clearError();
        this.render();

        try {
          const locale = this.config.locale || '';
          const bundle = await ProductSubscriptionBundle.fetchBundleData(this.config.bundleId, locale);
          this.bundle = bundle;
          this.products = this.normalizeBundleProducts(bundle);
          await this.hydrateProductDescriptions();
          this.purchaseType = bundle.purchaseType === 'ONETIME' ? 'one-time' : 'subscription';
          this.maxTotal = this.resolveMaxTotal();
          this.selectedSellingPlanId = this.defaultSellingPlanId();
          this.renderProducts();
          this.renderFrequencyOptions();
          this.render();
        } catch (error) {
          console.error('Unable to load Loop bundle data.', error);
          this.setError(this.t('errorBundleLoad'));
        } finally {
          this.bundleLoading = false;
          this.render();
        }
      }

      resolveMaxTotal() {
        const boxSizes = (this.bundle && this.bundle.boxSizes ? this.bundle.boxSizes : []);
        const configuredMax = boxSizes.reduce((max, item) => {
          const value = Number(item.maxSize || 0);
          return value > max ? value : max;
        }, 0);
        if (configuredMax > 0) return configuredMax;

        const fallbackMin = boxSizes.reduce((max, item) => {
          const value = Number(item.minSize || 0);
          return value > max ? value : max;
        }, 0);
        return fallbackMin > 0 ? fallbackMin : Number(this.config.fallbackMaxTotal || this.selectedServingQty || 3);
      }

      resolveMinRequiredQty() {
        const cardQuantities = this.servingCards
          .map((card) => Number(card.getAttribute('data-serving-card')))
          .filter((value) => Number.isFinite(value) && value > 0);

        return cardQuantities.length ? Math.min(...cardQuantities) : 1;
      }

      defaultSellingPlanId() {
        const plans = this.bundle && this.bundle.sellingPlans ? this.bundle.sellingPlans : [];
        const defaultPlan = plans.find((plan) => Number(plan.isDefault) === 1);
        return defaultPlan ? Number(defaultPlan.shopifyId || defaultPlan.id) : this.firstSellingPlanId();
      }

      firstSellingPlanId() {
        const plans = this.bundle && this.bundle.sellingPlans ? this.bundle.sellingPlans : [];
        return plans[0] ? Number(plans[0].shopifyId || plans[0].id) : null;
      }

      renderFrequencyOptions() {
        if (!this.frequencySelect) return;
        const plans = this.bundle && this.bundle.sellingPlans ? this.bundle.sellingPlans : [];
        this.frequencySelect.innerHTML = plans.map((plan) => {
          const value = Number(plan.shopifyId || plan.id);
          const selected = value === this.selectedSellingPlanId ? ' selected' : '';
          const label = this.translatePlanName(plan);
          return `<option value="${value}"${selected}>${label}</option>`;
        }).join('');
      }

      // Loop returns plan names like "Deliver every 30 days" in English
      // regardless of storefront locale. We rebuild the label from the
      // plan's interval data using the translated label_every_* schema
      // settings. The field names below match Loop's storefront API:
      // deliveryInterval ("DAY" | "WEEK" | "MONTH") + deliveryIntervalCount.
      // (Older Loop responses may use interval/intervalCount — we read
      // both for forward/backward compatibility.)
      translatePlanName(plan) {
        const interval = String(plan.deliveryInterval || plan.interval || plan.intervalUnit || '').toUpperCase();
        const count = Number(plan.deliveryIntervalCount || plan.intervalCount || plan.intervalValue || 1);

        if (interval === 'WEEK' || interval === 'WEEKS') {
          const key = count === 1 ? 'everyWeek' : 'everyWeeks';
          if (this.strings[key]) return interpolate(this.strings[key], { count });
        }
        if (interval === 'MONTH' || interval === 'MONTHS') {
          const key = count === 1 ? 'everyMonth' : 'everyMonths';
          if (this.strings[key]) return interpolate(this.strings[key], { count });
        }
        if (interval === 'DAY' || interval === 'DAYS') {
          const key = count === 1 ? 'everyDay' : 'everyDays';
          if (this.strings[key]) return interpolate(this.strings[key], { count });
        }
        return String(plan.name || '').trim();
      }

      setQuantity(key, nextValue) {
        const safeValue = Math.max(0, nextValue);
        const currentValue = this.quantities[key] || 0;
        const currentTotal = this.totalQuantity();
        const proposedTotal = currentTotal - currentValue + safeValue;

        if (proposedTotal > this.maxTotal) {
          this.quantities[key] = Math.max(0, currentValue + (this.maxTotal - currentTotal));
        } else {
          this.quantities[key] = safeValue;
        }

        const nextTotal = this.totalQuantity();
        if (nextTotal < currentTotal) {
          this.selectedServingQty = nextTotal > 0 ? nextTotal : null;
        }

        this.clearError();
        this.render();
      }

      totalQuantity() {
        return this.products.reduce((total, product) => total + (this.quantities[String(product.position)] || 0), 0);
      }

      resetQuantities() {
        this.quantities = {};
      }

      normalizeBundleProducts(bundle) {
        return ((bundle && bundle.products) || []).map((product, index) => {
          const variant = (product.variants || [])[0] || {};
          const position = index + 1;

          return {
            position,
            productId: Number(product.shopifyId || product.shopifyProductId),
            variantId: Number(variant.shopifyId),
            handle: product.handle || product.productHandle || '',
            title: product.title || product.productTitle || '',
            description: this.resolveProductDescription(product),
            image: variant.imageURL || variant.image || product.imageURL || product.image || '',
            priceCents: Math.round(Number(variant.price || 0) * 100),
            outOfStock: Boolean(variant.outOfStock),
            limits: Array.isArray(product.limits) ? product.limits : []
          };
        });
      }

      resolveProductDescription(product) {
        const directDescription = typeof product.description === 'string' ? product.description.trim() : '';
        if (directDescription) return directDescription;

        return '';
      }

      async hydrateProductDescriptions() {
        // Hydrate from Shopify's products/{handle}.js endpoint. Two reasons:
        // 1. Loop's storefront API doesn't return product descriptions, only handles.
        // 2. Shopify's endpoint is locale-aware — on /fr/ pages it returns the
        //    French title automatically (Shopify.routes.root resolves to "/fr/"),
        //    which fixes the case where Loop returns English titles even on
        //    a French storefront. The override map only covers plan descriptions;
        //    product titles come straight from Shopify per-locale.
        const productsToHydrate = this.products.filter((product) => product.handle);
        if (!productsToHydrate.length) return;

        await Promise.all(productsToHydrate.map(async (product) => {
          const [productData, productMeta] = await Promise.all([
            ProductSubscriptionBundle.fetchProductData(product.handle),
            ProductSubscriptionBundle.fetchProductMeta(product.handle)
          ]);

          if (!productData) return;

          // Always prefer the Shopify-side title when available, since it
          // reflects the active storefront locale. Fall back to Loop's title
          // (already on product.title) if the fetch failed or returned nothing.
          const localizedTitle = typeof productData.title === 'string' ? productData.title.trim() : '';
          if (localizedTitle) product.title = localizedTitle;

          const shortDescription = productMeta && typeof productMeta.shortDescription === 'string'
            ? this.decodeHtml(productMeta.shortDescription).trim()
            : '';

          if (shortDescription) {
            product.description = shortDescription;
          } else if (!product.description) {
            const description = this.extractProductDescription(productData);
            if (description) product.description = description;
          }
        }));
      }

      extractProductDescription(productData) {
        const rawDescription = productData && typeof productData.description === 'string'
          ? productData.description
          : '';

        if (!rawDescription) return '';

        const plainText = rawDescription
          .replace(/<br\s*\/?>/gi, ' ')
          .replace(/<\/p>/gi, ' ')
          .replace(/<[^>]+>/g, ' ')
          .replace(/&nbsp;/gi, ' ')
          .replace(/&amp;/gi, '&')
          .replace(/\s+/g, ' ')
          .trim();

        if (!plainText) return '';
        return plainText.length > 120 ? `${plainText.slice(0, 117).trim()}...` : plainText;
      }

      // Escape values that are interpolated into the renderProducts HTML
      // template literal — product titles and descriptions come from
      // external data (Loop API + product.js) so they need escaping.
      escapeHtml(value) {
        return String(value == null ? '' : value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      }

      decodeHtml(value) {
        if (value == null || value === '') return '';
        const textarea = document.createElement('textarea');
        textarea.innerHTML = String(value);
        return textarea.value;
      }

      renderProducts() {
        if (!this.productsContainer) return;

        const addLabel = this.escapeHtml(this.t('addProduct'));
        const addPlus = this.escapeHtml(this.t('addPlus'));
        const decreaseLabel = this.escapeHtml(this.t('decreaseQuantity'));
        const increaseLabel = this.escapeHtml(this.t('increaseQuantity'));

        this.productsContainer.innerHTML = this.products.map((product) => {
          const safeTitle = this.escapeHtml(product.title);
          const descriptionHtml = product.description
            ? `<div class="product-subscription-bundle__product-description">${this.escapeHtml(product.description)}</div>`
            : '';
          const imageHtml = product.image
            ? `<img src="${this.escapeHtml(product.image)}" alt="${safeTitle}" loading="lazy" width="64" height="64">`
            : '';

          return `
            <div class="product-subscription-bundle__product${product.outOfStock ? ' is-disabled' : ''}" data-bundle-product="${product.position}">
              <div class="product-subscription-bundle__product-media">
                ${imageHtml}
              </div>
              <div class="product-subscription-bundle__product-content">
                <div class="product-subscription-bundle__product-title">${safeTitle}</div>
                ${descriptionHtml}
              </div>
              <div class="product-subscription-bundle__qty" data-qty-control="${product.position}">
                <button type="button" class="product-subscription-bundle__qty-add" data-qty-add="${product.position}" aria-label="${addLabel}">
                  <span>${addPlus}</span>
                </button>
                <div class="product-subscription-bundle__qty-stepper" data-qty-stepper="${product.position}">
                  <button type="button" class="product-subscription-bundle__qty-button" data-qty-minus="${product.position}" aria-label="${decreaseLabel}">−</button>
                  <span class="product-subscription-bundle__qty-value" data-qty-value="${product.position}">0</span>
                  <button type="button" class="product-subscription-bundle__qty-button" data-qty-plus="${product.position}" aria-label="${increaseLabel}">+</button>
                </div>
              </div>
            </div>
          `;
        }).join('');
      }

      selectedPlan() {
        const plans = this.bundle && this.bundle.sellingPlans ? this.bundle.sellingPlans : [];
        return plans.find((plan) => Number(plan.shopifyId || plan.id) === this.selectedSellingPlanId) || null;
      }

      discountMatchesPurchaseType(discount) {
        const bundlePurchaseType = String(this.bundle && this.bundle.purchaseType ? this.bundle.purchaseType : '').toUpperCase();
        const purchaseType = String(discount && discount.purchaseType ? discount.purchaseType : 'ALL').toUpperCase();

        if (bundlePurchaseType === 'ALL') return true;
        if (purchaseType === 'ALL') return true;
        if (purchaseType === 'SUBSCRIPTION') return this.purchaseType === 'subscription';
        if (purchaseType === 'ONETIME') return this.purchaseType === 'one-time';

        return false;
      }

      bundleDiscount(totalQty) {
        const discounts = [...((this.bundle && this.bundle.discounts) ? this.bundle.discounts : [])]
          .filter((discount) => this.discountMatchesPurchaseType(discount))
          .sort((left, right) => {
            const rightValue = Number(right.minCartQuantity || right.minConditionValue || 0);
            const leftValue = Number(left.minCartQuantity || left.minConditionValue || 0);
            return rightValue - leftValue;
          });

        return discounts.find((discount) => totalQty >= Number(discount.minCartQuantity || discount.minConditionValue || 0)) || null;
      }

      bundleDiscountAmountCents(baseSubtotal, totalQty) {
        const discount = this.bundleDiscount(totalQty);
        if (!discount || baseSubtotal <= 0) return 0;

        const discountValue = Number(discount.value || 0);
        const discountType = String(discount.type || '').toUpperCase();

        if (discountType === 'PERCENTAGE') {
          return Math.round(baseSubtotal * discountValue / 100);
        }

        return Math.round(discountValue * 100);
      }

      sellingPlanPercent(plan) {
        const directDiscount = plan && plan.discounts ? plan.discounts[0] : null;
        if (directDiscount && directDiscount.type === 'PERCENTAGE') {
          return Number(directDiscount.value || 0);
        }

        const policy = plan && plan.pricingPolicies ? plan.pricingPolicies[0] : null;
        return Number(policy && policy.adjustmentValue ? policy.adjustmentValue.percentage || 0 : 0);
      }

      maxSellingPlanPercent() {
        const plans = this.bundle && this.bundle.sellingPlans ? this.bundle.sellingPlans : [];
        return plans.reduce((max, plan) => {
          const pct = this.sellingPlanPercent(plan);
          return pct > max ? pct : max;
        }, 0);
      }

      subscribeTitleLabel(quantity) {
        const baseSubtotal = this.subtotalForQuantity(quantity);
        const currentCents = this.priceForQuantity(quantity, 'subscription');
        if (!baseSubtotal || currentCents >= baseSubtotal) return this.t('subscribeAndSave');

        const savingsPercent = Math.round(((baseSubtotal - currentCents) / baseSubtotal) * 100);
        return savingsPercent > 0 ? `${savingsPercent}%` : '';
      }

      unitPriceCents() {
        if (!this.products.length) return 0;
        return Math.round(this.products.reduce((sum, p) => sum + p.priceCents, 0) / this.products.length);
      }

      servingSummary(quantity) {
        const baseSubtotal = this.unitPriceCents() * quantity;
        if (!quantity || baseSubtotal <= 0) return { currentCents: 0, compareCents: 0 };

        const bundleDiscountCents = this.bundleDiscountAmountCents(baseSubtotal, quantity);
        const plan = this.selectedPlan();
        const sellingPlanDiscountCents = this.purchaseType === 'subscription'
          ? Math.round(baseSubtotal * this.sellingPlanPercent(plan) / 100)
          : 0;

        return {
          currentCents: Math.max(0, baseSubtotal - bundleDiscountCents - sellingPlanDiscountCents),
          compareCents: baseSubtotal
        };
      }

      servingDiscountLabel(quantity) {
        const summary = this.servingSummary(quantity);
        if (!summary.compareCents || summary.compareCents <= summary.currentCents) return '';

        const savingsPercent = Math.round(((summary.compareCents - summary.currentCents) / summary.compareCents) * 100);
        return savingsPercent > 0 ? this.t('save', { percent: savingsPercent }) : '';
      }

      selectedSubtotal() {
        return this.products.reduce((total, product) => {
          const qty = this.quantities[String(product.position)] || 0;
          return qty === 0 ? total : total + product.priceCents * qty;
        }, 0);
      }

      subtotalForQuantity(quantity) {
        return this.unitPriceCents() * quantity;
      }

      priceForQuantity(quantity, purchaseType) {
        const baseSubtotal = this.subtotalForQuantity(quantity);
        if (!quantity || baseSubtotal <= 0) return 0;

        const bundleDiscountCents = this.bundleDiscountAmountCents(baseSubtotal, quantity);
        const plan = this.selectedPlan();
        const planDiscountCents = purchaseType === 'subscription'
          ? Math.round(baseSubtotal * this.sellingPlanPercent(plan) / 100)
          : 0;

        return Math.max(0, baseSubtotal - bundleDiscountCents - planDiscountCents);
      }

      renderSubtitles() {
        if (!this.bundle) {
          if (this.subscribeSubtitle) this.subscribeSubtitle.textContent = '';
          if (this.onetimeSubtitle) this.onetimeSubtitle.textContent = '';
          return;
        }

        const quantity = this.resolveActiveServingQty();
        const servings = quantity * Number(this.config.servingsPerPack || 30);
        const subCents = this.priceForQuantity(quantity, 'subscription');
        const otCents = this.priceForQuantity(quantity, 'one-time');

        if (this.subscribeSubtitle) {
          this.subscribeSubtitle.textContent = this.t('servingsSummary', {
            price: this.formatMoney(Math.round(subCents / servings)),
            count: servings
          });
        }
        if (this.onetimeSubtitle) {
          this.onetimeSubtitle.textContent = this.t('servingsSummary', {
            price: this.formatMoney(Math.round(otCents / servings)),
            count: servings
          });
        }
      }

      renderMainPrice() {
        if (!this.mainPriceEl || !this.bundle) return;

        const quantity = this.totalQty > 0 ? this.totalQty : this.resolveActiveServingQty();
        const baseSubtotal = this.subtotalForQuantity(quantity);
        const currentCents = this.totalQty > 0
          ? (() => {
              const plan = this.selectedPlan();
              const bundleDiscountCents = this.bundleDiscountAmountCents(baseSubtotal, this.totalQty);
              const planDiscountCents = this.purchaseType === 'subscription'
                ? Math.round(baseSubtotal * this.sellingPlanPercent(plan) / 100)
                : 0;
              return Math.max(0, baseSubtotal - bundleDiscountCents - planDiscountCents);
            })()
          : this.priceForQuantity(quantity, this.purchaseType);
        const compareCents = baseSubtotal > currentCents ? baseSubtotal : 0;

        const targetPriceEl = this.mainPriceEl.querySelector('.target-price');
        const targetCompareEl = this.mainPriceEl.querySelector('.target-compare-at-price');
        if (targetPriceEl) targetPriceEl.textContent = String(currentCents);
        if (targetCompareEl) targetCompareEl.textContent = compareCents > 0 ? String(compareCents) : '';

        const priceEl = this.mainPriceEl.querySelector('.price');
        const saleEl = this.mainPriceEl.querySelector('.price-sale');
        const compareEl = this.mainPriceEl.querySelector('.price-compare');
        const formattedPrice = this.formatMoney(currentCents);
        const formattedCompare = compareCents > 0 ? this.formatMoney(compareCents) : '';

        if (compareCents > 0) {
          if (priceEl) {
            priceEl.outerHTML = `<span class="price-sale selection:bg-text-[rgb(var(--colors-price-sale),0.2)] ml-1">${formattedPrice}</span>`;
          } else if (saleEl) {
            saleEl.textContent = formattedPrice;
          }

          const nextSaleEl = this.mainPriceEl.querySelector('.price-sale');
          if (compareEl) {
            compareEl.textContent = formattedCompare;
          } else if (nextSaleEl) {
            nextSaleEl.insertAdjacentHTML('beforebegin', `<s class="cap rtl:inline-block price-compare rtl:leading-tight text-[80%]">${formattedCompare}</s>`);
          }
        } else {
          if (saleEl) {
            saleEl.outerHTML = `<p class="price">${formattedPrice}</p>`;
          } else if (priceEl) {
            priceEl.textContent = formattedPrice;
          }

          const nextCompareEl = this.mainPriceEl.querySelector('.price-compare');
          if (nextCompareEl) nextCompareEl.remove();
        }
      }

      formatMoney(cents) {
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
          return window.Shopify.formatMoney(cents, this.root.dataset.moneyFormat);
        }
        return new Intl.NumberFormat('en-CA', {
          style: 'currency',
          currency: this.root.dataset.shopCurrency || (this.bundle && this.bundle.currencyCode) || 'CAD'
        }).format(cents / 100);
      }

      render() {
        const isBusy = this.loading || this.bundleLoading;
        this.totalQty = this.totalQuantity();

        this.root.classList.toggle('is-loading', this.bundleLoading);
        if (this.loadingEl) this.loadingEl.hidden = !this.bundleLoading;

        const activeServingQty = this.resolveActiveServingQty();

        this.servingCards.forEach((card) => {
          const quantity = Number(card.getAttribute('data-serving-card'));
          const summary = this.servingSummary(quantity);
          const priceEl = card.querySelector(`[data-serving-price="${quantity}"]`);
          const compareEl = card.querySelector(`[data-serving-compare="${quantity}"]`);
          const discountEl = card.querySelector(`[data-serving-discount="${quantity}"]`);
          const discountLabel = this.servingDiscountLabel(quantity);
          const isActive = activeServingQty === quantity;

          card.classList.toggle('is-active', isActive);
          if (priceEl) priceEl.textContent = summary.currentCents > 0 ? this.formatMoney(summary.currentCents) : '';
          if (compareEl) compareEl.textContent = summary.compareCents > summary.currentCents ? this.formatMoney(summary.compareCents) : '';
          if (discountEl) {
            discountEl.textContent = discountLabel || '';
            discountEl.classList.toggle('is-empty', !discountLabel);
          }
        });

        this.products.forEach((product) => {
          const key = String(product.position);
          const quantity = this.quantities[key] || 0;
          const controlEl = this.root.querySelector(`[data-qty-control="${key}"]`);
          const addEl = this.root.querySelector(`[data-qty-add="${key}"]`);
          const stepperEl = this.root.querySelector(`[data-qty-stepper="${key}"]`);
          const valueEl = this.root.querySelector(`[data-qty-value="${key}"]`);
          const minusEl = this.root.querySelector(`[data-qty-minus="${key}"]`);
          const plusEl = this.root.querySelector(`[data-qty-plus="${key}"]`);
          const productEl = this.root.querySelector(`[data-bundle-product="${key}"]`);
          const isZeroState = quantity <= 0;

          if (valueEl) valueEl.textContent = String(quantity);
          if (controlEl) controlEl.classList.toggle('is-active', !isZeroState);
          if (addEl) {
            addEl.disabled = this.totalQty >= this.maxTotal || isBusy || product.outOfStock;
          }
          if (minusEl) minusEl.disabled = quantity <= 0 || isBusy;
          if (plusEl) plusEl.disabled = this.totalQty >= this.maxTotal || isBusy || product.outOfStock;
          if (productEl) productEl.classList.toggle('is-active', quantity > 0);
        });

        this.purchaseCards.forEach((card) => {
          card.classList.toggle('is-active', card.getAttribute('data-purchase-card') === this.purchaseType);
        });

        this.purchaseButtons.forEach((button) => {
          button.classList.toggle('has-items', this.totalQty > 0);
        });

        if (this.subscribeTitle && this.bundle) {
          this.subscribeTitle.textContent = this.subscribeTitleLabel(activeServingQty);
        }

        this.renderSubtitles();
        this.renderMainPrice();

        if (this.frequencyWrap) {
          const shouldHide = this.purchaseType !== 'subscription' || !((this.bundle && this.bundle.sellingPlans) || []).length;
          this.frequencyWrap.classList.toggle('is-hidden', shouldHide);
        }

        if (this.planDescription) {
          const plan = this.selectedPlan();
          const description = this.purchaseType === 'subscription'
            ? resolvePlanDescription(plan, this.config.locale, this.config.subscriptionPlanDescription)
            : '';
          this.planDescription.innerHTML = description;
        }

        const canSubmit = !isBusy && !!this.bundle;
        if (this.atcButton) this.atcButton.disabled = !canSubmit;
        if (this.atcButton) this.atcButton.classList.toggle('is-submitting', this.loading);
        if (this.atcText) this.atcText.hidden = this.loading;
        if (this.atcSpinner) this.atcSpinner.hidden = !this.loading;
        if (this.atcText) {
          this.atcText.textContent = this.totalQty === 0
            ? this.t('emptyStateAtc')
            : this.totalQty >= this.minRequiredQty
              ? this.t('addToCart')
              : this.t('addYourFlavours');
        }

        if (this.summaryEl && this.summaryPriceEl && this.summaryCompareEl) {
          const baseSubtotal = this.selectedSubtotal();
          const bundleDiscountCents = this.bundleDiscountAmountCents(baseSubtotal, this.totalQty);
          const plan = this.selectedPlan();
          const planDiscountCents = this.purchaseType === 'subscription'
            ? Math.round(baseSubtotal * this.sellingPlanPercent(plan) / 100)
            : 0;
          const currentCents = Math.max(0, baseSubtotal - bundleDiscountCents - planDiscountCents);
          const hasSummary = this.totalQty > 0 && currentCents > 0;

          this.summaryEl.hidden = !hasSummary;
          this.summaryPriceEl.textContent = hasSummary ? this.formatMoney(currentCents) : '';
          this.summaryCompareEl.textContent = hasSummary && baseSubtotal > currentCents ? this.formatMoney(baseSubtotal) : '';

          if (this.totalQty < this.minRequiredQty) {
            this.summaryEl.classList.add('soft-hide');
          } else {
            this.summaryEl.classList.remove('soft-hide');
          }
        }
      }

      resolveActiveServingQty() {
        if (this.totalQty > 0) {
          if (this.selectedServingQty && this.totalQty <= this.selectedServingQty) {
            return this.selectedServingQty;
          }

          return Math.min(this.totalQty, this.maxTotal);
        }

        if (this.selectedServingQty) return this.selectedServingQty;
        return Number(this.config.defaultServingQty || 3);
      }

      selectedProducts() {
        return this.products.map((product) => {
          return { ...product, quantity: this.quantities[String(product.position)] || 0 };
        }).filter((product) => product.quantity > 0);
      }

      resolveLoopVariant(product) {
        const loopVariant = this.findLoopVariant(product);
        return Number(loopVariant.shopifyId || product.variantId);
      }

      findLoopVariant(product) {
        const products = this.bundle && this.bundle.products ? this.bundle.products : [];
        const loopProduct = products.find((item) => {
          return Number(item.shopifyId || item.shopifyProductId) === Number(product.productId) || item.handle === product.handle;
        });
        if (!loopProduct || !loopProduct.variants || !loopProduct.variants.length) return {};
        return loopProduct.variants[0];
      }

      resolveBoxSizeId(selectedProducts) {
        const productWithLimit = selectedProducts.find((product) => {
          return Array.isArray(product.limits) && product.limits.some((limit) => limit && limit.boxSizeId);
        });

        if (!productWithLimit) return null;

        const matchedLimit = productWithLimit.limits.find((limit) => limit && limit.boxSizeId);
        return matchedLimit && matchedLimit.boxSizeId ? String(matchedLimit.boxSizeId) : null;
      }

      async addBundleToCart() {
        if (this.loading || this.totalQty < this.minRequiredQty || !this.bundle) return;

        const selectedProducts = this.selectedProducts();
        const boxSizeId = this.resolveBoxSizeId(selectedProducts);
        const selectedPlan = this.selectedPlan();
        const selectedPlanId = this.purchaseType === 'subscription' ? this.selectedSellingPlanId : null;
        const discount = this.bundleDiscount(this.totalQty);

        if (!boxSizeId) {
          this.setError(this.t('errorBoxSize'));
          return;
        }

        if (!selectedProducts.length) {
          this.setError(this.t('errorSelectFlavour'));
          return;
        }

        if (this.totalQty < this.minRequiredQty) {
          this.setError(this.t('errorSelectFlavour', { count: this.minRequiredQty }));
          return;
        }

        this.loading = true;
        this.clearError();
        this.render();

        try {
          const cartHelper = window.Alpine && window.Alpine.store('xCartHelper') ? window.Alpine.store('xCartHelper') : null;
          const miniCart = window.Alpine && window.Alpine.store('xMiniCart') ? window.Alpine.store('xMiniCart') : null;
          const sectionIds = cartHelper ? cartHelper.getSectionsToRender().map((s) => s.id) : [];
          const loopBundleId = this.config.bundleId;
          const transactionEndpoint = `https://api.loopsubscriptions.com/storefront/2023-10/bundle/${encodeURIComponent(loopBundleId)}/transaction`;
          const loopAccessToken = this.resolveLoopAccessToken();
          const transactionPayload = {
            boxSizeId: String(boxSizeId),
            discountId: discount ? String(discount.id || discount.bundleDiscountId || discount.nanoId) : null,
            sellingPlanShopifyId: selectedPlanId ? Number(selectedPlanId) : null,
            productVariants: selectedProducts.map((product) => {
              const loopVariant = this.findLoopVariant(product);
              const payloadVariant = {
                shopifyId: Number(loopVariant.shopifyId || product.variantId),
                quantity: product.quantity
              };
              if (loopVariant.productVariantId) payloadVariant.productVariantId = loopVariant.productVariantId;
              return payloadVariant;
            }),
            quantity: this.totalQty
          };
          const transactionHeaders = {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          };

          if (loopAccessToken) {
            transactionHeaders.Authorization = `Bearer ${loopAccessToken}`;
          }

          const transactionResponse = await fetch(transactionEndpoint, {
            method: 'POST',
            headers: transactionHeaders,
            body: JSON.stringify(transactionPayload)
          });
          const transactionContentType = transactionResponse.headers.get('content-type') || '';
          const transactionData = transactionContentType.includes('application/json')
            ? await transactionResponse.json()
            : { message: await transactionResponse.text() };
          const txnId = transactionData && transactionData.data
            ? (transactionData.data.loopBundleGuid || transactionData.data.txnId || transactionData.data.bundleTransactionId)
            : transactionData.txnId;

          if (!transactionResponse.ok || !txnId) {
            throw new Error((transactionData && transactionData.message) || this.t('errorTransaction'));
          }

          const items = selectedProducts.map((product) => {
            const item = {
              id: this.resolveLoopVariant(product),
              quantity: product.quantity,
              properties: {
                _bundleId: txnId,
                _bundleName: this.bundle.name
              }
            };
            if (selectedPlan && selectedPlanId) item.selling_plan = Number(selectedPlanId);
            return item;
          });

          const cartResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items,
              sections: sectionIds,
              sections_url: window.location.pathname
            })
          });
          const cartData = await cartResponse.json();

          if (!cartResponse.ok || cartData.status === 422) {
            throw new Error(cartData.description || cartData.message || this.t('errorAddToCart'));
          }

          if (cartHelper && cartData.sections) {
            cartHelper.reRenderSections(cartData.sections);
            const bubble = document.getElementById('cart-icon-bubble');
            if (bubble) {
              cartHelper.currentItemCount = parseInt(bubble.innerHTML, 10);
            }
            document.dispatchEvent(new CustomEvent('eurus:cart-drawer:rerender'));
            document.dispatchEvent(new CustomEvent('cart:updated'));
          }

          if (window.Alpine && window.Alpine.store('xCartNoti') && window.Alpine.store('xCartNoti').enable) {
            window.Alpine.store('xCartNoti').setItem(cartData);
          } else if (miniCart) {
            miniCart.loading = true;
            miniCart.openCart();
            document.dispatchEvent(new CustomEvent('eurus:cart:redirect'));
          }

          document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));

          this.syncCartState({
            discountCode: discount && discount.code ? discount.code : '',
            cartHelper,
            sectionIds,
            miniCart
          });
        } catch (error) {
          console.error('Subscription bundle add to cart failed.', error);
          this.setError(error.message || this.t('errorAddToCart'));
        } finally {
          this.loading = false;
          this.render();
        }
      }

      async syncCartState({ discountCode, cartHelper, sectionIds, miniCart }) {
        try {
          if (discountCode) {
            await fetch(`${window.Shopify.routes.root}discount/${encodeURIComponent(discountCode)}`, {
              method: 'GET',
              credentials: 'same-origin'
            });
          }

          const updatedCart = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sections: sectionIds,
              sections_url: window.location.pathname
            })
          }).then((response) => response.json());

          window.Loop = window.Loop || {};
          window.Loop.bundleCartAllItems = updatedCart.items || [];

          if (cartHelper && updatedCart.sections) {
            cartHelper.reRenderSections(updatedCart.sections);
            const bubble = document.getElementById('cart-icon-bubble');
            if (bubble) {
              cartHelper.currentItemCount = parseInt(bubble.innerHTML, 10);
            }
            document.dispatchEvent(new CustomEvent('eurus:cart-drawer:rerender'));
            document.dispatchEvent(new CustomEvent('cart:updated'));
          }

          if (typeof initLoopBundle === 'function') {
            try { initLoopBundle('LOOP_fr-hellodarknessmyoldfriend_bundles_drawer'); } catch (_) {}
          }
        } catch (error) {
          console.error('Subscription bundle cart sync failed.', error);
        } finally {
          if (miniCart) miniCart.loading = false;
        }
      }

      setError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.classList.remove('hidden');
      }

      clearError() {
        if (!this.errorEl) return;
        this.errorEl.textContent = '';
        this.errorEl.classList.add('hidden');
      }

      resolveLoopAccessToken() {
        const configToken = this.config && this.config.loopAccessToken ? String(this.config.loopAccessToken).trim() : '';
        if (configToken) return configToken;

        if (window.Loop && typeof window.Loop.accessToken === 'string' && window.Loop.accessToken.trim()) {
          return window.Loop.accessToken.trim();
        }

        if (typeof window.loopCustomerAccessToken === 'string' && window.loopCustomerAccessToken.trim()) {
          return window.loopCustomerAccessToken.trim();
        }

        try {
          const storedToken = window.localStorage.getItem('loop-access-token');
          if (storedToken && storedToken.trim()) return storedToken.trim();
        } catch (error) {
          console.warn('Unable to read Loop access token from localStorage.', error);
        }

        return '';
      }
    }

    const initBundles = (scope = document) => {
      scope.querySelectorAll(selector).forEach((root) => {
        if (root.dataset.initialized === 'true') return;
        new ProductSubscriptionBundle(root);
      });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => initBundles());
    } else {
      initBundles();
    }

    document.addEventListener('shopify:section:load', (event) => {
      initBundles(event.target);
    });
  })();
}
